import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import { Suspense, createContext, useCallback, useMemo, useState } from "react";
import { Stack } from "@mui/system";
import IChildrenProps from "models/common-props/IChildrenProps";
import { ConfirmationDialogType } from "constants/confirmationDialogType";
import useTranslate from "hooks/useTranslate";
import React from "react";
import { useThemeColors, useIsDarkMode } from "theme";
import Button from "components/Button";
import { buildTestId } from "helpers/testId";

/**
 * DOM contract for the single app-wide confirmation dialog: the paper is
 * `confirm-dialog`, its heading `confirm-dialog-title`, its body
 * `confirm-dialog-description`, and the buttons `confirm-dialog-confirm` /
 * `confirm-dialog-cancel`. `data-confirm-type` mirrors the dialog type so a
 * spec can assert a DELETE prompt without reading translated copy. The dialog
 * renders in a portal at the document root, so scope to the paper.
 */
const TEST_ID = "confirm-dialog";
const ConfirmationDialogIcon = React.lazy(
  () => import("components/icons/ConfirmationDialogIcon"),
);

const ConfirmationDialogContext = createContext({
  setConfirmAction: (
    messageHeader: string,
    messageDescription: string,
    action: Function,
    messageType: ConfirmationDialogType,
    showConfirm: boolean = true,
    closeBtnTextKey?: string,
    performActionAndCloseBtnTextKey?: string,
    performActionAndCloseBtnPrimaryColor?: boolean,
  ) => {},
});

export function ConfirmationDialogContextProvider({
  children,
}: IChildrenProps) {
  const { buttonTrans } = useTranslate("");
  const [open, setOpen] = useState(false);
  const [messageHeader, setMessageHeader] = useState("");
  const [messageDescription, setMessageDescription] = useState("");
  const [action, setAction] = useState<Function | null>(null);
  const [type, setType] = useState<ConfirmationDialogType>("");
  const [closeBtnText, setCloseBtnText] = useState<string | undefined>(
    undefined,
  );

  const [performActionAndCloseBtnText, setPerformActionAndCloseBtnText] =
    useState<string | undefined>(undefined);
  const [
    performActionAndCloseBtnPrimaryColor,
    setPerformActionAndCloseBtnPrimaryColor,
  ] = useState<Boolean | undefined>(undefined);

  const colors = useThemeColors();
  const isDark = useIsDarkMode();

  function performActionAndClose() {
    if (action) {
      action();
      setOpen(false);
    }

    handleClose();
  }

  function handleClose() {
    setOpen(false);
    setAction(null);
  }

  const setConfirmAction = useCallback(
    (
      messageHeader: string,
      messageDescription: string,
      action: Function,
      messageType: ConfirmationDialogType,
      showConfirm: boolean = true,
      closeBtnTextKey?: string,
      performActionAndCloseBtnTextKey?: string,
      performActionAndCloseBtnPrimaryColor?: boolean,
    ) => {
      if (showConfirm) {
        setOpen(true);
        setMessageHeader(messageHeader);
        setMessageDescription(messageDescription);
        setAction(() => action);
        setType(messageType);
        setCloseBtnText(closeBtnTextKey);
        setPerformActionAndCloseBtnText(performActionAndCloseBtnTextKey);
        setPerformActionAndCloseBtnPrimaryColor(
          performActionAndCloseBtnPrimaryColor,
        );
      } else {
        action();
      }
    },
    [],
  );

  const context = useMemo(() => ({ setConfirmAction }), [setConfirmAction]);

  return (
    <ConfirmationDialogContext.Provider value={context}>
      {children}
      <Dialog
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            "data-testid": TEST_ID,
            "data-confirm-type": type || undefined,
          } as any,
        }}
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": {
              width: "100%",
              maxWidth: "500px",
            },
          },
        }}
      >
        <Stack
          sx={{
            alignItems: "center",
            position: "relative"
          }}>
          <DialogTitle
            id="confirm-dialog-title"
            style={{
              borderRadius: "24px",
              boxShadow: colors.components.card.shadow,
              backgroundColor: colors.components.dialog.background,
              position: "absolute",
              top: "-50px",
              padding: "10px",
            }}
          >
            <Suspense fallback={<div>Loading ...</div>}>
              <ConfirmationDialogIcon iconType={type} isDark={isDark} />
            </Suspense>
          </DialogTitle>
        </Stack>

        <Stack sx={{
          alignItems: "center"
        }}>
          <DialogContent
            sx={{
              mt: "60px",
              display: "flex",
              flexDirection: "column",
              textAlign: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 600,
                marginBottom: "3px",
                color: colors.text.primary,
              }}
              data-testid={buildTestId(TEST_ID, "title")}
            >
              {messageHeader}
            </Typography>

            {messageDescription && (
              <DialogContentText
                id="confirm-dialog-description"
                sx={{
                  paddingTop: "5px",
                  fontSize: "18px",
                  textAlign: "center",
                  color: colors.text.secondary,
                }}
                data-testid={buildTestId(TEST_ID, "description")}
              >
                {messageDescription}
              </DialogContentText>
            )}
          </DialogContent>

          <DialogActions sx={{ marginBottom: "24px" }}>
            <Stack direction={"row"} spacing={2}>
              <Button
                variant={
                  performActionAndCloseBtnPrimaryColor !== undefined
                    ? performActionAndCloseBtnPrimaryColor
                      ? "primary"
                      : "error"
                    : type === "DELETE"
                    ? "error"
                    : "primary"
                }
                onClick={performActionAndClose}
                data-testid={buildTestId(TEST_ID, "confirm")}
                sx={{
                  minWidth: "140px",
                }}
              >
                {performActionAndCloseBtnText ??
                  buttonTrans(
                    type === "WARNING" ? "discardChanges" : "yesDelete",
                  )}
              </Button>
              <Button
                variant="cancel"
                onClick={handleClose}
                data-testid={buildTestId(TEST_ID, "cancel")}
                sx={{
                  minWidth: "140px",
                }}
              >
                {closeBtnText ??
                  buttonTrans(type === "WARNING" ? "stayHere" : "noCancel")}
              </Button>
            </Stack>
          </DialogActions>
        </Stack>
      </Dialog>
    </ConfirmationDialogContext.Provider>
  );
}

export default ConfirmationDialogContext;
